/**
 * CSS Variable Utilities
 * Converts SDK config to CSS custom properties
 */

import type { SDKConfig, Theme, Layout, Effects, Typography } from '../types';
import { getBorderRadiusCss, getSpacingCss, getBodySizeCss } from '../types';

/**
 * Convert camelCase to kebab-case
 */
export function camelToKebab(str: string): string {
  return str.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
}

/**
 * Convert theme colors to CSS variables
 */
export function themeColorsToCss(colors: Theme['colors']): Record<string, string> {
  const vars: Record<string, string> = {};

  Object.entries(colors).forEach(([key, value]) => {
    vars[`--ks-${camelToKebab(key)}`] = value;
  });

  return vars;
}

/**
 * Convert full theme to CSS variables
 */
export function themeToCss(theme: Theme): Record<string, string> {
  return {
    ...themeColorsToCss(theme.colors),
    '--ks-mode': theme.mode,
    '--ks-radius': getBorderRadiusCss(theme.borderRadius),
  };
}

/**
 * Convert typography to CSS variables
 */
export function typographyToCss(typography: Typography): Record<string, string> {
  return {
    '--ks-font-family': typography.fontFamily,
    '--ks-heading-weight': String(typography.headingWeight),
    '--ks-body-size': getBodySizeCss(typography.bodySize),
  };
}

/**
 * Convert layout to CSS variables
 */
export function layoutToCss(layout: Layout): Record<string, string> {
  return {
    '--ks-columns': String(layout.columns),
    '--ks-spacing': getSpacingCss(layout.spacing),
    '--ks-card-style': layout.cardStyle,
    '--ks-layout-style': layout.style,
  };
}

/**
 * Convert effects to CSS classes/variables
 */
export function effectsToCss(effects: Effects): Record<string, string> {
  const transitionMap: Record<string, string> = {
    none: '0ms',
    subtle: '150ms',
    smooth: '300ms',
    bouncy: '400ms cubic-bezier(0.68, -0.55, 0.265, 1.55)',
  };

  return {
    '--ks-hover-animation': effects.hoverAnimation,
    '--ks-transition': transitionMap[effects.transitions] || '300ms',
  };
}

/**
 * Convert full config to CSS variables
 */
export function configToCssVars(config: SDKConfig): Record<string, string> {
  return {
    ...themeToCss(config.theme),
    ...typographyToCss(config.typography),
    ...layoutToCss(config.layout),
    ...effectsToCss(config.effects),
  };
}

/**
 * Apply CSS variables to an element
 */
export function applyCssVars(
  element: HTMLElement,
  vars: Record<string, string>
): void {
  Object.entries(vars).forEach(([key, value]) => {
    element.style.setProperty(key, value);
  });
}

/**
 * Apply SDK config to a Shadow DOM host element
 */
export function applyConfigToHost(host: HTMLElement, config: SDKConfig): void {
  const vars = configToCssVars(config);
  applyCssVars(host, vars);

  // Add data attributes for CSS selectors
  host.dataset.ksMode = config.theme.mode;
  host.dataset.ksCardStyle = config.layout.cardStyle;
  host.dataset.ksHover = config.effects.hoverAnimation;
}

/**
 * Generate CSS string from variables (for stylesheet injection)
 */
export function generateCssVarsString(config: SDKConfig): string {
  const vars = configToCssVars(config);
  const varStrings = Object.entries(vars)
    .map(([key, value]) => `${key}: ${value};`)
    .join('\n  ');

  return `:host {\n  ${varStrings}\n}`;
}
